// frontend/src/components/client/mobile/MobileChat.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Send, User, CheckCheck } from 'lucide-react';
import { sendClientMessage } from '../../../services/clientApi';

const MobileChat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [staffOnline, setStaffOnline] = useState(false);
  const wsRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    connect();
    return () => wsRef.current?.close();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const connect = () => {
    const token = localStorage.getItem('client_access_token');
    if (!token) return;
    const base = (process.env.REACT_APP_API_URL || 'http://localhost:8001')
      .replace(/^https/, 'wss').replace(/^http/, 'ws').replace(/\/api\/?$/, '');
    wsRef.current = new WebSocket(`${base}/api/client/chat/ws?token=${token}`);
    wsRef.current.onopen = () => { setStaffOnline(true); setLoading(false); };
    wsRef.current.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        setMessages(prev => [...prev, { id: d.id, message: d.message, sender_type: d.sender_type, created_at: d.created_at, is_read: d.is_read }]);
      } catch {}
    };
    wsRef.current.onclose = (e) => {
      setStaffOnline(false);
      if (e.code !== 1008 && e.code !== 1011) setTimeout(connect, 3000);
    };
    wsRef.current.onerror = () => setLoading(false);
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();
    const tempId = `t_${Date.now()}`;
    setSending(true);
    setInput('');
    setMessages(prev => [...prev, { id: tempId, message: msg, sender_type: 'client', created_at: new Date().toISOString(), is_read: false, is_temp: true }]);
    try {
      const res = await sendClientMessage(msg);
      setMessages(prev => prev.map(m => m.id === tempId ? { ...res.data, is_temp: false } : m));
    } catch {
      setMessages(prev => prev.filter(m => m.id !== tempId));
      alert('Send error, please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (ts) => {
    const d = new Date(ts), diff = Date.now() - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}min`;
    if (diff < 86400000) return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('en-US');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 56px - env(safe-area-inset-top) - 72px)' }}>
      {/* Online indicator */}
      <div style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 6, borderBottom: '0.5px solid #1e293b', flexShrink: 0 }}>
        <div style={{ width: 8, height: 8, borderRadius: 4, background: staffOnline ? '#22c55e' : '#475569', boxShadow: staffOnline ? '0 0 6px #22c55e' : 'none' }} />
        <span style={{ fontSize: 12, color: '#64748b' }}>{staffOnline ? 'Support online' : 'Support offline'}</span>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
            <div style={{ width: 24, height: 24, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          </div>
        ) : messages.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#475569', gap: 8 }}>
            <div style={{ width: 52, height: 52, borderRadius: 16, background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <User size={24} color="#475569" />
            </div>
            <p style={{ fontSize: 14, textAlign: 'center' }}>No messages<br /><span style={{ fontSize: 12 }}>Send a message to get started</span></p>
          </div>
        ) : messages.map((msg) => {
          const isClient = msg.sender_type === 'client';
          return (
            <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isClient ? 'flex-end' : 'flex-start' }}>
              {!isClient && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <div style={{ width: 22, height: 22, borderRadius: 11, background: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <User size={12} color="#fff" />
                  </div>
                  <span style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600 }}>FBO Support</span>
                  <span style={{ fontSize: 10, color: '#475569' }}>{formatTime(msg.created_at)}</span>
                </div>
              )}
              <div style={{
                maxWidth: '78%',
                background: isClient ? '#2563eb' : '#1e293b',
                borderRadius: isClient ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                padding: '10px 14px',
                border: isClient ? 'none' : '0.5px solid #334155',
              }}>
                <p style={{ fontSize: 14, color: '#fff', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{msg.message}</p>
              </div>
              {isClient && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
                  <span style={{ fontSize: 10, color: '#475569' }}>{formatTime(msg.created_at)}</span>
                  {!msg.is_temp && <CheckCheck size={12} color={msg.is_read ? '#3b82f6' : '#475569'} />}
                </div>
              )}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input bar — stuck to bottom, above tab bar */}
      <div style={{
        padding: '10px 12px',
        paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
        borderTop: '0.5px solid #1e293b',
        display: 'flex', alignItems: 'flex-end', gap: 8,
        background: 'rgba(15,23,42,0.97)',
        flexShrink: 0,
      }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => {
            setInput(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
          }}
          placeholder="Message…"
          rows={1}
          style={{
            flex: 1, background: '#1e293b', border: '0.5px solid #334155',
            borderRadius: 20, padding: '10px 14px', color: '#f1f5f9',
            fontSize: 15, resize: 'none', outline: 'none',
            maxHeight: 100, overflowY: 'auto', lineHeight: 1.4,
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          }}
        />
        <button
          onClick={send}
          disabled={sending || !input.trim()}
          style={{
            width: 40, height: 40, borderRadius: 20, flexShrink: 0,
            background: input.trim() ? '#2563eb' : '#1e293b',
            border: 'none', cursor: input.trim() ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s',
          }}
        >
          {sending
            ? <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            : <Send size={17} color={input.trim() ? '#fff' : '#475569'} />
          }
        </button>
      </div>
    </div>
  );
};

export default MobileChat;